import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import BrandMark from "@/components/BrandMark";
import { useLocale } from "@/lib/locale";

/**
 * The emotional moment: right after a presence is created, we call the
 * opening endpoint which generates the very first message from them.
 * This screen lets that message *arrive*, slowly, before letting the user
 * reply in the full conversation view.
 */
export default function PresenceArrives() {
    const { id } = useParams();
    const location = useLocation();
    const nav = useNavigate();
    const { t } = useLocale();
    const [presence, setPresence] = useState(location.state?.presence || null);
    const [phase, setPhase] = useState("lighting");
    const [opening, setOpening] = useState(null);
    const [conversationId, setConversationId] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                if (!presence) {
                    const { data } = await api.get(`/presences/${id}`);
                    if (!cancelled) setPresence(data);
                }
                await new Promise((r) => setTimeout(r, 1400));
                if (cancelled) return;
                setPhase("arriving");
                // If the user landed here from the dashboard's today-
                // recurrence banner, forward that context so the presence's
                // opening line is quietly aware of *today*.
                const recCtx = location.state?.recurrence_context;
                const { data } = await api.post(
                    `/presences/${id}/open-conversation`,
                    recCtx ? { recurrence_context: recCtx } : undefined,
                );
                if (cancelled) return;
                setOpening(data.opening_message);
                setConversationId(data.conversation_id);
                await new Promise((r) => setTimeout(r, 1200));
                if (cancelled) return;
                setPhase("revealed");
            } catch (e) {
                if (cancelled) return;
                setErr(e?.response?.data?.detail || t("arrives.err"));
                setPhase("error");
            }
        })();
        return () => { cancelled = true; };
    }, [id]); // eslint-disable-line

    const enterConversation = () => {
        if (!conversationId) return;
        nav(`/chat/${conversationId}`, { state: { presence, skipOpeningFetch: true } });
    };

    return (
        <div className="grain min-h-screen candle-halo flex flex-col relative overflow-hidden">
            {/* Ambient "memory" atmosphere — ray + veil + drifting dust, behind content */}
            <div className="arrives-atmosphere" aria-hidden="true" data-testid="arrives-atmosphere">
                <div className={`arrives-ray ${phase === "revealed" ? "flared" : ""}`} />
                <div className="arrives-veil" />
                <div className="dust-field absolute inset-0">
                    <span className="dust-mote a" style={{ left: "22%", top: "0%", animationDelay: "0s" }} />
                    <span className="dust-mote b" style={{ left: "18%", top: "0%", animationDelay: "-6s" }} />
                    <span className="dust-mote c" style={{ left: "30%", top: "0%", animationDelay: "-12s" }} />
                    <span className="dust-mote a" style={{ left: "14%", top: "0%", animationDelay: "-18s", width: "1.5px", height: "1.5px" }} />
                    <span className="dust-mote b" style={{ left: "26%", top: "0%", animationDelay: "-3s", width: "3px", height: "3px" }} />
                    <span className="dust-mote c" style={{ left: "34%", top: "0%", animationDelay: "-9s" }} />
                </div>
            </div>
            <Header minimal />
            <main className="flex-1 max-w-3xl w-full mx-auto px-6 md:px-12 py-16 flex flex-col items-center justify-center text-center animate-fade-in-slow relative z-10">
                <div className="mb-14">
                    <BrandMark size={100} ceremonial />
                </div>
                {/* Photo */}
                <div className={`photo-frame aspect-[3/4] w-72 md:w-96 mb-12 relative overflow-hidden photo-halo-strong ${phase === "revealed" ? "presence-pulse" : ""}`}>
                    {presence?.photo_base64 ? (
                        <img
                            src={`data:${presence.photo_mime};base64,${presence.photo_base64}`}
                            alt={presence.name}
                            className={`w-full h-full object-cover transition-all duration-[2000ms] ${phase === "revealed" ? "grayscale-0" : "grayscale-[50%]"}`}
                        />
                    ) : (
                        <div className="w-full h-full bg-ink-surfaceUp flex items-center justify-center">
                            <BrandMark monogram size={144} className={`transition-opacity duration-[2000ms] ${phase === "revealed" ? "opacity-90" : "opacity-55"}`} />
                        </div>
                    )}
                    <div
                        className={`absolute inset-0 bg-ink-deep transition-opacity duration-[2000ms] ${phase === "revealed" ? "opacity-0" : phase === "arriving" ? "opacity-25" : "opacity-45"}`}
                        aria-hidden
                    />
                </div>

                {/* Name */}
                <p className="label-eyebrow text-base">{presence?.relationship}</p>
                <h1 className="h-serif text-5xl md:text-6xl mt-3 mb-10">{presence?.name}</h1>

                {/* Status text */}
                {phase === "lighting" && (
                    <p className="text-ink-textMid font-serif italic text-xl" data-testid="arrives-lighting">{t("arrives.lighting")}</p>
                )}
                {phase === "arriving" && (
                    <p className="text-ink-gold font-serif italic text-xl animate-pulse" data-testid="arrives-arriving">{t("arrives.arriving")}</p>
                )}

                {/* The first message, once revealed */}
                {phase === "revealed" && opening && (
                    <div className="w-full max-w-xl mt-4 animate-fade-in" data-testid="arrives-message">
                        <p className="label-eyebrow mb-3 text-ink-gold">{t("arrives.first_message")}</p>
                        <div className="bg-ink-surface p-8 md:p-10 rounded-sm border-l-[3px] border-ink-gold text-left shadow-[0_4px_30px_-10px_rgba(0,0,0,0.55)]">
                            <p className="font-serif text-[1.7rem] md:text-[2rem] leading-[1.55] text-ink-textHigh whitespace-pre-wrap">
                                {opening.content}
                            </p>
                        </div>
                        <button
                            onClick={enterConversation}
                            className="btn-primary mt-12 !px-9 !py-4 text-lg"
                            data-testid="arrives-enter-chat"
                        >
                            {t("arrives.reply")}
                        </button>
                        <p className="text-base text-ink-textMid italic mt-5">
                            {t("arrives.wait")}
                        </p>
                    </div>
                )}

                {phase === "error" && (
                    <div className="max-w-lg mt-6" data-testid="arrives-error">
                        <p className="text-ink-danger font-serif italic text-lg">{err}</p>
                        <button onClick={() => nav(`/presence/${id}`)} className="btn-secondary mt-6">{t("arrives.go_profile")}</button>
                    </div>
                )}
            </main>
        </div>
    );
}
